import { GraphQLContext } from '@moonpig/web-core-graphql'
import { Metrics, logger } from '@moonpig/web-core-monitoring'
import { timeout } from '@moonpig/web-core-utils'
import { DateTime } from 'luxon'
import { GetUpcomingMissionButtonRemindersGQL } from '../../components/MissionButton/RemindersButton/queries/getUpcomingMissionButtonReminders'
import {
  GetUpcomingMissionButtonRemindersQuery,
  GetUpcomingMissionButtonRemindersQueryVariables,
} from '../../components/MissionButton/RemindersButton/queries/__generated__/getUpcomingMissionButtonReminders'
import type { MissionButtonReminder } from '../../components/MissionButton/RemindersButton/types'
import { getRemindersWithOccasions } from '../../modules/MissionButtons/getRemindersWithOccasions'
import { filterSupportedOccasions } from '../../components/MissionButton/RemindersButton/utils'

const REMINDERS_TO_TAKE = 5
const OCCASIONS_DAYS_TO_DISPLAY = 30
const REMINDERS_QUERY_TIMEOUT_MS = 1500

type RemindersQueryInput = {
  isExperimentEnabled: boolean
  isAuthenticated: boolean
  metrics: Metrics
}

export type RemindersQueryResult =
  | { reminders: MissionButtonReminder[] }
  | { timedOut: true }

export const remindersQuery = async (
  graphQL: { query: GraphQLContext['query'] },
  { isExperimentEnabled, isAuthenticated, metrics }: RemindersQueryInput,
): Promise<RemindersQueryResult> => {
  if (!isExperimentEnabled || !isAuthenticated) {
    return { reminders: [] }
  }

  const currentDateTime = DateTime.utc()
  const endDateTime = currentDateTime.plus({ days: OCCASIONS_DAYS_TO_DISPLAY })

  try {
    const { data } = await metrics.traceAsync(
      {
        traceName: 'reminders-mission-button-query',
        metricName: 'reminders-mission-button-query',
        metricDimensions: {
          action: 'getRemindersMissionButton',
        },
      },
      () =>
        timeout(
          REMINDERS_QUERY_TIMEOUT_MS,
          graphQL.query<
            GetUpcomingMissionButtonRemindersQuery,
            GetUpcomingMissionButtonRemindersQueryVariables
          >({
            query: GetUpcomingMissionButtonRemindersGQL,
            variables: {
              remindersRequest: {
                startDate: currentDateTime.toISODate(),
                endDate: endDateTime.toISODate(),
                take: REMINDERS_TO_TAKE,
              },
              isAuthenticated,
            },
          }),
        ),
    )

    const reminders = data?.reminders ?? []

    const { remindersWithOccasions } = getRemindersWithOccasions({
      reminders,
      currentDateTime,
      endDateTime,
    })

    return { reminders: filterSupportedOccasions(remindersWithOccasions) }
  } catch (error) {
    const isTimeout =
      error instanceof Error &&
      /Promise timeout \(\d+ms elapsed\)/.test(error.message)
    logger.fixToday(
      isTimeout
        ? 'Reminders mission button query timed out'
        : 'Error thrown while querying reminders mission button',
      {},
      error,
    )
    return isTimeout ? { timedOut: true } : { reminders: [] }
  }
}
